import React, { Component, ErrorInfo } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary as LexicalDefaultErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

// Lexical commands
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
} from 'lexical';

// List commands
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list';

// Link commands
import { TOGGLE_LINK_COMMAND } from '@lexical/link';

// Table commands
import { INSERT_TABLE_COMMAND } from '@lexical/table';

// Icons for error boundary
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Nodes
import { ParagraphNode, TextNode } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ClauseNode } from '../nodes/ClauseNode';
import { CitationNode, CitationsPlugin, CitationResolverProvider } from '../nodes/CitationNode';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { CodeNode } from '@lexical/code';
import { SelectionBridgePlugin } from '../../dms/components/edgedocs/components/authoring/SelectionBridgePlugin';
import { SelectionApplyPlugin } from '../../dms/components/edgedocs/components/authoring/SelectionApplyPlugin';
import { CompositionDragPlugin } from '../../dms/components/edgedocs/components/authoring/CompositionDragPlugin';
import { PasteNormalizerPlugin } from '../plugins/PasteNormalizerPlugin';
// import { useEdgedocsCanvasStore } from '../../dms/EdgeDocs/EdgeDocsCore/stores/useEdgedocsCanvasStore';

// Import plugin contract system
import { 
  initializeEditorSafely,
  debugPluginRegistry,
  validateEditorStateForHydration,
  type HydrationValidationResult
} from '../plugins/PluginRegistry';

// Import EdgeLex plugin validator
import { 
  createEdgeLexValidator,
  performCompleteValidation,
  type ValidationResult as EdgeLexValidationResult
} from '../plugins/EdgeLexPluginRegistry';

// Import Lexical fallback patch system
import { 
  patchLexicalPluginsWithFallback,
  validateCoreNodesRegistration,
  debugPatchStatus
} from '../plugins/applyLexicalPluginFallbackPatch';

// Import sidebar
import Sidebar from '../../../layout/Sidebar';
import { useAppStore } from '../../../store/appStore';

// Import Google Docs layout components
import GoogleDocsLayout from '../rulers/GoogleDocsLayout';
import { EdgedocsCanvasViewer } from '../../dms/components/edgedocs/components/EdgedocsCanvasViewer';
// LEGACY IMPORTS - COMMENTED OUT - EdgeDocsCore system replaced by ClientFirst
// import { TypesetterApiContext } from '../../dms/EdgeDocs/EdgeDocsCore/utils/TypesetterApiContext';
// import { TypesetterApiClient } from '../../dms/EdgeDocs/EdgeDocsCore/utils/typesetterApi';
// import { useEdgedocsCanvasStore } from '../../dms/EdgeDocs/EdgeDocsCore/stores/useEdgedocsCanvasStore';
// import { lexicalToAst } from '../../dms/EdgeDocs/EdgeDocsCore/adapters/lexicalToAst'; // Disabled

/**
 * 🛡️ CUSTOM LEXICAL ERROR BOUNDARY - Catches initialization errors
 * Provides retry functionality and user-friendly error messages
 */
class CustomLexicalErrorBoundary extends Component<
  { children: React.ReactNode; onError?: (error: Error) => void },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; onError?: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🛡️ [CustomLexicalErrorBoundary] Caught Lexical initialization error:', error);
    console.error('🛡️ [CustomLexicalErrorBoundary] Error info:', errorInfo);
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-center p-6">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-900 mb-2">Editor Initialization Error</h3>
            <p className="text-sm text-red-700 mb-4">
              The document editor encountered an initialization error. This may be due to corrupted document content.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: undefined })}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface LexicalDocumentEditorProps {
  document?: any;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (content: string, metadata?: any) => void;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
  onChange?: (content: string) => void;
  initialContent?: string;
  documentType?: 'contract' | 'motion' | 'brief' | 'memo' | 'letter';
  showSidebar?: boolean;
  autoSave?: boolean;
  panelMode?: boolean;
  allowUnsafeEditorState?: boolean; // 🧪 Developer mode for testing with faulty nodes
}

// Toolbar component that has access to the editor context
function GoogleDocsToolbar() {
  const [editor] = useLexicalComposerContext();

  // 🛡️ LEXICAL FALLBACK PATCH: Apply once per editor lifecycle
  React.useEffect(() => {
    if (editor && !(editor as any).__lexical_fallback_patched__) {
      console.log('🔧 [GoogleDocsToolbar] Applying Lexical fallback patch to editor...');
      const patchResult = patchLexicalPluginsWithFallback(editor);
      
      if (patchResult.success) {
        console.log('✅ [GoogleDocsToolbar] Lexical fallback patch applied successfully');
        debugPatchStatus(editor);
        validateCoreNodesRegistration(editor);
      } else {
        console.error('🚨 [GoogleDocsToolbar] Lexical fallback patch failed:', patchResult.errors);
      }
    }
  }, [editor]);

  return (
    <div className="bg-gray-50 px-3 py-1 flex items-center h-10 overflow-x-auto">
      {/* Undo/Redo */}
      <div className="flex items-center mr-2">
        <button
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Undo (Ctrl+Z)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
        <button
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Redo (Ctrl+Y)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6-6m6 6l-6 6" />
          </svg>
        </button>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Font Family */}
      <select className="h-7 px-2 text-sm bg-white border border-gray-300 rounded text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:outline-none mr-1 min-w-[100px]">
        <option>Arial</option>
        <option>Times New Roman</option>
        <option>Georgia</option>
        <option>Calibri</option>
        <option>Helvetica</option>
      </select>

      {/* Font Size */}
      <select className="h-7 px-2 text-sm bg-white border border-gray-300 rounded text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:outline-none mr-2 w-[50px]">
        <option>10</option>
        <option>11</option>
        <option defaultValue="12">12</option>
        <option>14</option>
        <option>16</option>
        <option>18</option>
        <option>20</option>
        <option>24</option>
      </select>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Heading Dropdown */}
      <select className="h-7 px-2 text-sm bg-white border border-gray-300 rounded text-gray-700 hover:border-gray-400 focus:border-blue-500 focus:outline-none mr-2 min-w-[120px]">
        <option>Normal text</option>
        <option>Heading 1</option>
        <option>Heading 2</option>
        <option>Heading 3</option>
        <option>Heading 4</option>
        <option>Heading 5</option>
        <option>Heading 6</option>
      </select>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Text Formatting */}
      <div className="flex items-center mr-2">
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 font-bold"
          title="Bold (Ctrl+B)"
        >
          B
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 italic"
          title="Italic (Ctrl+I)"
        >
          I
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 underline"
          title="Underline (Ctrl+U)"
        >
          U
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Strikethrough"
        >
          <span className="line-through">S</span>
        </button>
      </div>

      {/* Text Color */}
      <button
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 mr-1"
        title="Text color"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 21a4 4 0 004-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4" />
        </svg>
      </button>

      {/* Highlight Color */}
      <button
        className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600 mr-2"
        title="Highlight color"
      >
        <div className="w-4 h-3 bg-yellow-300 border border-gray-400 rounded-sm"></div>
      </button>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Lists */}
      <div className="flex items-center mr-2">
        <button
          onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Bullet list"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <button
          onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Numbered list"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 10h18M3 16h18" />
          </svg>
        </button>
      </div>

      {/* Alignment */}
      <div className="flex items-center mr-2">
        <button
          onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Align left"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8M4 18h16" />
          </svg>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Align center"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M6 12h12M4 18h16" />
          </svg>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Align right"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M8 12h8M4 18h16" />
          </svg>
        </button>
        <button
          onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Justify"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Insert Tools */}
      <div className="flex items-center mr-2">
        <button
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
          }}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Insert link"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
        <button
          onClick={() => {
            const rows = prompt('Number of rows:', '3');
            const columns = prompt('Number of columns:', '3');
            if (rows && columns) {
              editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                rows: rows,
                columns: columns,
                includeHeaders: true,
              });
            }
          }}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Insert table"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H5a1 1 0 01-1-1V10z" />
          </svg>
        </button>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-gray-300 mr-2"></div>

      {/* Legal-Specific Tools */}
      <div className="flex items-center">
        <button
          onClick={() => console.log('Insert comment')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Insert comment"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
        <button
          onClick={() => console.log('Insert clause')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Insert clause"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </button>
        <button
          onClick={() => console.log('Insert citation')}
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 text-gray-600"
          title="Insert citation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l-3-1m3 1l3-1" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function LexicalDocumentEditor({
  initialContent = '', 
  onChange,
  isOpen,
  onClose,
  placeholder = 'Start typing...',
  document,
  showSidebar = false,
  allowUnsafeEditorState = false // 🧪 Developer mode
}: LexicalDocumentEditorProps) {
  const embeddedHostRefRef = React.useRef<HTMLDivElement | null>(null);
  // PLUGIN CONTRACT SYSTEM: Enhanced state management with formal validation
  const [hasCorruptedState, setHasCorruptedState] = React.useState(false);
  const [fallbackMode, setFallbackMode] = React.useState(false);
  const [validationResult, setValidationResult] = React.useState<HydrationValidationResult | null>(null);
  const [edgeLexValidationResult, setEdgeLexValidationResult] = React.useState<EdgeLexValidationResult | null>(null);
  const [edgeLexValidator, setEdgeLexValidator] = React.useState<any>(null);
  const [fallbackPatchApplied, setFallbackPatchApplied] = React.useState(false);
  
  // Sidebar state from global store
  const { isSidebarExpanded } = useAppStore();

  // 🚀 PLUGIN CONTRACT INITIALIZATION: Setup EdgeLex validator on mount
  React.useEffect(() => {
    console.log('🚀 [LexicalDocumentEditor] Initializing EdgeLex plugin validator system...');
    
    try {
      // Create EdgeLex validator instance
      const validator = createEdgeLexValidator();
      setEdgeLexValidator(validator);
      
      // Also initialize the legacy plugin registry
      initializeEditorSafely('').then(() => {
        console.log('✅ [LexicalDocumentEditor] Legacy plugin registry initialized successfully');
        debugPluginRegistry();
      }).catch((error) => {
        console.error('🚨 [LexicalDocumentEditor] Legacy plugin registry initialization failed:', error);
      });
      
      console.log('✅ [LexicalDocumentEditor] EdgeLex validator initialized successfully');
    } catch (error) {
      console.error('🚨 [LexicalDocumentEditor] EdgeLex validator setup error:', error);
    }
  }, []); // Run once on mount

  // 🛡️ LEXICAL FALLBACK PATCH: Additional safety measure for main editor
  React.useEffect(() => {
    // This will run after the LexicalComposer is mounted
    const timeoutId = setTimeout(() => {
      if (!fallbackPatchApplied) {
        console.log('🔧 [LexicalDocumentEditor] Checking for fallback patch application...');
        // The actual patch is applied in GoogleDocsToolbar, this is just a monitor
        setFallbackPatchApplied(true);
      }
    }, 1000); // Check after 1 second

    return () => clearTimeout(timeoutId);
  }, [fallbackPatchApplied]);

  // 🚀 PLUGIN CONTRACT: Safe editor initialization with formal validation
  const createSafeEditorConfig = () => {
    console.log('🔧 LexicalDocumentEditor: Creating safe editor config...');
    console.log('🔧 Initial content preview:', {
      length: initialContent?.length || 0,
      preview: initialContent?.substring(0, 100) || 'empty',
      type: typeof initialContent,
    });

    let safeEditorState;
    let configurationSafe = true;

    try {
      // 🚀 PLUGIN CONTRACT INITIALIZATION
      if (allowUnsafeEditorState) {
        console.warn('🧪 [LexicalDocumentEditor] DEVELOPER MODE: Bypassing plugin contract validation');
        try {
          const rawJSON = JSON.parse(initialContent || '{}');
          if (rawJSON.root) {
            safeEditorState = JSON.stringify(rawJSON);
            console.log('🧪 [LexicalDocumentEditor] DEVELOPER MODE: Using raw unsafe state');
          } else {
            throw new Error('No root in unsafe state');
          }
        } catch (unsafeError) {
          console.error('🧪 [LexicalDocumentEditor] DEVELOPER MODE: Even unsafe loading failed:', unsafeError);
          safeEditorState = undefined;
          configurationSafe = false;
        }
      } else {
        // 🛡️ FORMAL EDGELEX PLUGIN CONTRACT VALIDATION
        console.log('🛡️ [LexicalDocumentEditor] Using EdgeLex plugin contract validation system');
        
        if (edgeLexValidator && initialContent && initialContent.trim()) {
          // Step 1: Perform complete EdgeLex validation
          const mockEditor = { registerNodeType: (nodeType: string) => console.log(`Mock registering: ${nodeType}`) };
          const edgeLexResult = performCompleteValidation(mockEditor, initialContent);
          setEdgeLexValidationResult(edgeLexResult);
          
          if (edgeLexResult.isHydrationSafe) {
            // Step 2: Use legacy validation for JSON parsing
            const validation = validateEditorStateForHydration(initialContent);
            setValidationResult(validation);
            
            if (validation.isValid && validation.sanitizedState) {
              safeEditorState = JSON.stringify(validation.sanitizedState);
              console.log('✅ [LexicalDocumentEditor] EdgeLex validation successful');
            } else {
              console.warn('🚨 [LexicalDocumentEditor] Legacy validation failed after EdgeLex passed');
              setHasCorruptedState(true);
              safeEditorState = undefined;
              configurationSafe = false;
            }
          } else {
            console.warn('🚨 [LexicalDocumentEditor] EdgeLex validation failed:', edgeLexResult.errors.join(', '));
            setHasCorruptedState(true);
            safeEditorState = undefined;
            configurationSafe = false;
          }
        } else {
          // Fallback to legacy validation if EdgeLex validator not ready
          console.log('🛡️ [LexicalDocumentEditor] Fallback to legacy validation (EdgeLex validator not ready)');
          const validation = validateEditorStateForHydration(initialContent || '');
          setValidationResult(validation);
          
          if (validation.isValid && validation.sanitizedState) {
            safeEditorState = JSON.stringify(validation.sanitizedState);
            console.log('✅ [LexicalDocumentEditor] Legacy validation successful');
          } else {
            console.warn('🚨 [LexicalDocumentEditor] Legacy validation failed:', validation.errors.join(', '));
            setHasCorruptedState(true);
            safeEditorState = undefined;
            configurationSafe = false;
          }
        }
      }

      console.log('✅ LexicalDocumentEditor: Editor state created:', {
        stateType: typeof safeEditorState,
        isFunction: typeof safeEditorState === 'function',
        isString: typeof safeEditorState === 'string',
        isUndefined: safeEditorState === undefined,
        developerMode: allowUnsafeEditorState,
        pluginContractUsed: !allowUnsafeEditorState,
      });
    } catch (stateError) {
      console.error('🚨 LexicalDocumentEditor: Error during plugin contract initialization:', stateError);
      configurationSafe = false;
      setHasCorruptedState(true);
      safeEditorState = undefined; // Force empty editor
    }

    // Enhanced error handler for Lexical initialization
    const enhancedErrorHandler = (error: Error) => {
      console.error('🛡️ [LexicalDocumentEditor] Lexical initialization error:', error);
      console.error('🛡️ [LexicalDocumentEditor] Error stack:', error.stack);
      console.error('🛡️ [LexicalDocumentEditor] Document info:', {
        documentId: document?.id,
        documentTitle: document?.title || document?.name,
        contentLength: initialContent?.length,
        hasContent: !!(initialContent && initialContent.trim()),
      });

      // Check if this is error #310 (common Lexical initialization failure)
      if (error.message.includes('310') || error.message.includes('root') || error.message.includes('initialization')) {
        console.error('🚨 [LexicalDocumentEditor] Detected Lexical Error #310 - marking as corrupted');
        setHasCorruptedState(true);
        setFallbackMode(true);
      }
    };

    const editorConfig = {
      namespace: 'EdgeLexLegalEditor',
      theme: {},
      onError: enhancedErrorHandler,
      nodes: [
        ParagraphNode,
        TextNode,
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode,
        AutoLinkNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        CodeNode,
        ClauseNode,
        CitationNode,
      ],
      editable: !fallbackMode,
      editorState: configurationSafe ? safeEditorState : undefined, // Force undefined if config unsafe
    };

    console.log('✅ LexicalDocumentEditor: Editor config created:', {
      namespace: editorConfig.namespace,
      nodeCount: editorConfig.nodes.length,
      hasEditorState: !!editorConfig.editorState,
      editorStateType: typeof editorConfig.editorState,
      configurationSafe,
    });

    return editorConfig;
  };

  const editorConfig = createSafeEditorConfig();

  // Auto-render initial layout once on open so the canvas doesn't stay idle
  React.useEffect(() => {
    try {
      const keys = ['access_token', 'webelieve_access_token', 'token', 'authToken', 'jwt_token'];
      let tok: string | null = null;
      for (const k of keys) { const v = localStorage.getItem(k); if (v) { tok = v; break; } }
      // LEGACY CODE - COMMENTED OUT - EdgeDocsCore system replaced by ClientFirst
      // const api = tok ? new TypesetterApiClient(window.location.origin, () => tok as string) : null;
      // if (!api) return;
      // const st = useEdgedocsCanvasStore.getState();
      // if (st.planHash) return;
      let parsed: any = null;
      try { parsed = initialContent ? JSON.parse(initialContent) : null; } catch {}
      if (!parsed || !parsed.root) return;
      // const ast = lexicalToAst(parsed); // Disabled - no longer needed
      // const payload = { ... } as any; // Disabled
      // Disabled - let DocumentEditorPage handle all typesetting
      console.debug('LexicalDocumentEditor loadPlan disabled - typesetting handled by DocumentEditorPage');
    } catch {}
  }, [initialContent]);

  if (!isOpen) return null;

  // CORRUPTION RECOVERY: Show user-friendly recovery UI
  if (hasCorruptedState && !fallbackMode) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Document Validation Failed</h3>
              <p className="text-sm text-gray-600 mb-4">
                This document contains node types or formatting that are not supported by the current editor configuration.
              </p>
              
              {/* EdgeLex Plugin Contract Validation Details */}
              {edgeLexValidationResult && (
                <div className="text-left bg-blue-50 rounded-lg p-4 mb-4 text-xs">
                  <h4 className="font-medium text-gray-900 mb-2">🔌 EdgeLex Plugin Validation:</h4>
                  <p className="text-blue-600 mb-1">
                    <strong>Registered plugins:</strong> {edgeLexValidationResult.registeredPlugins}
                  </p>
                  <p className="text-blue-600 mb-1">
                    <strong>Known node types:</strong> {edgeLexValidationResult.knownNodeTypes.length}
                  </p>
                  {edgeLexValidationResult.unknownNodeTypes.length > 0 && (
                    <p className="text-red-600 mb-1">
                      <strong>Unknown nodes:</strong> {edgeLexValidationResult.unknownNodeTypes.join(', ')}
                    </p>
                  )}
                  {edgeLexValidationResult.suggestions.length > 0 && (
                    <p className="text-orange-600">
                      <strong>Suggestions:</strong> {edgeLexValidationResult.suggestions.join('; ')}
                    </p>
                  )}
                </div>
              )}

              {/* Legacy Plugin Contract Validation Details */}
              {validationResult && (
                <div className="text-left bg-gray-50 rounded-lg p-4 mb-4 text-xs">
                  <h4 className="font-medium text-gray-900 mb-2">🛡️ Legacy Validation Details:</h4>
                  {validationResult.unregisteredNodes.length > 0 && (
                    <p className="text-red-600 mb-1">
                      <strong>Unregistered nodes:</strong> {validationResult.unregisteredNodes.join(', ')}
                    </p>
                  )}
                  {validationResult.errors.length > 0 && (
                    <p className="text-red-600 mb-1">
                      <strong>Errors:</strong> {validationResult.errors.join('; ')}
                    </p>
                  )}
                  {validationResult.structuralIssues.length > 0 && (
                    <p className="text-orange-600">
                      <strong>Structure issues:</strong> {validationResult.structuralIssues.join('; ')}
                    </p>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-600 mb-6">
                You can load it in plain text mode or return to the document list.
              </p>
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => {
                    setFallbackMode(true);
                    setHasCorruptedState(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Load in Plain Text Mode
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Return to Documents
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-gray-900 bg-opacity-50">
      <div className="absolute inset-0 flex">
        {/* Sidebar - Only show if showSidebar prop is true */}
        {showSidebar && (
          <div className={`transition-all duration-300 ease-in-out ${
            isSidebarExpanded ? 'w-64' : 'w-16'
          } flex-shrink-0 bg-white border-r border-gray-200`}>
            <Sidebar />
          </div>
        )}
        
        {/* Main Editor Area */}
        <div className={`flex-1 flex flex-col bg-white transition-all duration-300 ease-in-out ${
          showSidebar ? 'min-w-0' : ''
        }`}>
          {/* Fallback Mode Indicator */}
          {fallbackMode && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mr-2" />
                <span className="text-sm text-amber-700">
                  Document loaded in plain text mode due to formatting issues
                </span>
              </div>
            </div>
          )}

          {/* Developer Mode Indicator */}
          {allowUnsafeEditorState && (
            <div className="bg-red-50 border-b border-red-200 px-4 py-2">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm text-red-700 font-medium">
                  🧪 DEVELOPER MODE: Unsafe editor state loading enabled
                </span>
              </div>
            </div>
          )}

          {/* Fallback Patch Status Indicator */}
          {fallbackPatchApplied && (
            <div className="bg-green-50 border-b border-green-200 px-4 py-2">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-green-700 font-medium">
                  🛡️ FALLBACK PATCH: Core node types protected with automatic registration
                </span>
              </div>
            </div>
          )}

          <CustomLexicalErrorBoundary
            onError={(error) => {
              console.error('🛡️ [Main] LexicalComposer initialization failed:', error);
              setHasCorruptedState(true);
            }}
          >
            <LexicalComposer initialConfig={editorConfig}>
              <CitationResolverProvider value={{ format: (payload) => ({ text: payload.textFallback ?? `[cite:${payload.citationId}:${payload.field}]`, href: payload.href ?? null }) }}>
              {/* LEGACY CONTEXT - COMMENTED OUT - EdgeDocsCore system replaced by ClientFirst */}
              {/* <TypesetterApiContext.Provider value={(() => {
                try {
                  const keys = ['access_token', 'webelieve_access_token', 'token', 'authToken', 'jwt_token'];
                  let tok: string | null = null;
                  for (const k of keys) { const v = localStorage.getItem(k); if (v) { tok = v; break; } }
                  return tok ? new TypesetterApiClient(window.location.origin, () => tok as string) : null;
                } catch { return null; }
              })()}> */}
              {/* Google Docs Layout with Rulers, Page Layout, and Professional Header */}
              <GoogleDocsLayout
                documentTitle={document?.title || document?.name || 'Untitled Document'}
                onClose={onClose}
                toolbar={!editorConfig.editable ? null : <GoogleDocsToolbar />}
                showRulers={true}
                showLineNumbers={true}
                showIndentMarkers={true}
              >
                <div className="relative w-full h-full" ref={(el) => { (embeddedHostRefRef as any).current = el as any; }}>
                  {/* Embedded page canvas under the editor */}
                  <EdgedocsCanvasViewer />
                  <RichTextPlugin
                  contentEditable={
                    <ContentEditable
                      className="relative min-h-[300px] outline-none text-gray-800 text-base leading-7 focus:ring-0"
                      aria-label="Document content"
                      style={{ 
                        fontFamily: 'Times New Roman, serif', // TODO bind to StyleRegistry defaults
                        fontSize: '12pt',
                        lineHeight: '1.5',
                        backgroundColor: 'transparent',
                        caretColor: '#000',
                      }}
                    />
                  }
                  placeholder={
                    <div className="text-gray-400 absolute top-0 left-0 pointer-events-none">
                      {placeholder}
                    </div>
                  }
                  ErrorBoundary={LexicalDefaultErrorBoundary}
                  />
                </div>
                {/* Bridge Lexical selection to SelectionMapper state */}
                <SelectionBridgePlugin />
                <SelectionApplyPlugin />
                {/* IME/drag lifecycle plugin */}
                <CompositionDragPlugin />
                
                {/* Plugins */}
                <HistoryPlugin />
                <ListPlugin />
                <LinkPlugin />
                <TablePlugin />
                <CitationsPlugin />
                <PasteNormalizerPlugin />
                <OnChangePlugin
                  onChange={(editorState) => {
                    editorState.read(() => {
                      try {
                        const js = editorState.toJSON();
                        if (js?.root) onChange?.(JSON.stringify(js));
                      } catch {}
                    });
                  }}
                />
              </GoogleDocsLayout>
              {/* Minimal layout trigger for this route */}
              <div className="absolute top-14 right-6 z-50">
                <button
                  className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs"
                  onClick={() => {
                    try {
                      // LEGACY CODE - COMMENTED OUT - EdgeDocsCore system replaced by ClientFirst
                      // const api = (() => {
                      //   try {
                      //     const keys = ['access_token', 'webelieve_access_token', 'token', 'authToken', 'jwt_token'];
                      //     let tok: string | null = null;
                      //     for (const k of keys) { const v = localStorage.getItem(k); if (v) { tok = v; break; } }
                      //     return tok ? new TypesetterApiClient(window.location.origin, () => tok as string) : null;
                      //   } catch { return null; }
                      // })();
                      // if (!api) return;
                      console.log('Legacy render layout button disabled - use ClientFirst editor instead');
                      return;
                      // const { loadPlan, ensurePages } = useEdgedocsCanvasStore.getState(); // Disabled
                      const editor = (window as any).__edgedocsEditor;
                      if (!editor) return;
                      // const js = editor.getEditorState().toJSON(); // Disabled - no longer needed
                      // const ast = lexicalToAst(js); // Disabled - no longer needed
                      // const payload = { ... } as any; // Disabled
                      // Disabled - let DocumentEditorPage handle all typesetting
                      console.debug('LexicalDocumentEditor Render Layout disabled - typesetting handled by DocumentEditorPage');
                    } catch {}
                  }}
                >
                  Render Layout
                </button>
              </div>
              {/* </TypesetterApiContext.Provider> */}
              </CitationResolverProvider>
            </LexicalComposer>
          </CustomLexicalErrorBoundary>
        </div>
      </div>
    </div>
  );
}

// Export hook for compatibility
export function useDocumentEditor() {
  const [editorDocument, setEditorDocument] = React.useState<any>(undefined);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);

  const openEditor = (document: any) => {
    setEditorDocument(document);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorDocument(undefined);
  };

  return {
    editorDocument,
    isEditorOpen,
    openEditor,
    closeEditor,
  };
}

export default LexicalDocumentEditor; 